<script lang="ts" setup>
import { computed, inject, ref } from 'vue'
import type { ScrollChapter } from '../composables/useInfiniteChapter'
import ScrollChapterSubscribePanel from './ScrollChapterSubscribePanel.vue'
import type { ChapterConfig } from '@/composables/payment'
import { CHARGE_TYPE } from '@/constants'
import { useI18n } from '@/composables/useI18n'

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'unlock'): void
}>()

declare const __BRAND__: 'rocnovel' | 'oceread' | 'ficmoon' | 'novelarc' | 'storyella' | 'silktale' | 'pearla' | 'fictora' | 'marinovel' | 'talewave'

interface Props {
  chapter: ScrollChapter
  memberCoin: number
  contentId: string
}

const config = inject<ChapterConfig>('chapterConfig')!
const autoLock = ref(true)
const { t } = useI18n()
const isTalewave = __BRAND__ === 'talewave'
const showSubscribePanel = computed(() => props.chapter.priceAmount > props.memberCoin)

function handleUnlock() {
  emit('unlock')
}

function toggleAutoLock() {
  autoLock.value = !autoLock.value
}

defineExpose({
  autoLock,
})
</script>

<template>
  <div class="chapter-unlock relative pt-40px text-center">
    <!-- 渐变遮罩覆盖上方内容 -->
    <div class="mask" />

    <!-- 解锁区域 -->
    <div class="unlock-content">
      <template v-if="isTalewave && showSubscribePanel">
        <ScrollChapterSubscribePanel
          :content-id="contentId"
          :content-chapter-id="chapter.id"
          :config="config"
          :chapter="chapter"
          @fallback-unlock="handleUnlock"
        />
      </template>

      <template v-else>
        <h3 class="theme-h3 font-bold text-18px text-center text-#32353A">
          {{ config.texts.unlockTitle }}
        </h3>
        <p class="theme-desc mt-8px lh-24px text-14px text-#65686F text-center" style="white-space: pre-line;">
          {{ config.texts.unlockDesc }}
        </p>

        <a
          class="theme-btn inline-block mt-24px px-24px py-3 min-h-40px lh-1em text-15px text-white text-center btn-solid rounded-full w-300px"
          @click.stop="handleUnlock"
        >
          <span v-if="chapter.priceAmount !== undefined">
            <span v-if="chapter.feeType === CHARGE_TYPE.CHAPTER">{{ t('CoinsToRead2', { 0: chapter.priceAmount }) }}</span>
            <span v-else>{{ t('CoinsToReadBook', { 0: chapter.priceAmount }) }}</span>
          </span>
        </a>

        <div class="theme-radio mt-10px flex justify-center items-center" @click.stop="toggleAutoLock">
          <div class="radio flex justify-center items-center w-14px h-14px inline-block border b-#9296A0 b-rd-14px cursor" :class="{ selected: autoLock }">
            <span class="inline-block w-6px h-6px bg-n-primary b-rd-6px" />
          </div>
          <span class="ml-6px label text-14px text-#65686F">{{ config.texts.autoUnlock }}</span>
        </div>

        <p class="theme-balance mt-24px pb-18px lh-24px text-14px text-#9296A0 text-center">
          {{ t('NovelBalance', { 0: memberCoin }) }}
        </p>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.radio {
  span {
    display: none;
  }

  &.selected {
    border-color: var(--primary-color);

    span {
      display: inline-block
    }
  }
}

.chapter-unlock {
  position: relative;

  .mask {
    position: absolute;
    top: -126px;
    left: 0;
    width: 100%;
    height: 126px;
    background: linear-gradient(to bottom, transparent, var(--theme-background, #F6F7F9));
    pointer-events: none;
  }

  .unlock-content {
    position: relative;
    z-index: 1;
  }
}
</style>
